import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Reveal } from './motion';
import { EASE } from './brand';

const ITEMS = [
  {
    q: 'How do people get accounts?',
    a: 'By invitation only \u2014 there is no open signup. A coach or admin sends an invite that already carries the person\u2019s role and team, and the registration link only works for that invitation. Nobody wanders in off the internet and joins your club.',
  },
  {
    q: 'Do we have to move the whole club over at once?',
    a: 'No. Teams and squads are independent, so most clubs start with one squad for a season and expand. Rosters, workouts and training plans can be bulk-imported from CSV, so bringing an existing team across is an afternoon, not a project.',
  },
  {
    q: 'Can one person belong to more than one club?',
    a: 'Yes, and with a different role in each. A masters rower who also coaches a junior programme has one login and switches between clubs; coaches on several teams switch team context the same way.',
  },
  {
    q: 'Where do the weather and river numbers come from?',
    a: 'An admin sets the club\u2019s location for weather and picks the USGS gauge that matters for your stretch of water. Air temperature, wind, water temperature and discharge are then pulled live onto every session card.',
  },
  {
    q: 'Can we make it look like our club?',
    a: 'Your club name, primary and secondary colors and logo apply across the app \u2014 including the browser tab title and favicon. Athletes see your program, not ours.',
  },
  {
    q: 'What is the difference between goal 2k and GMS?',
    a: 'Goal 2k is the target you or your coach set for that athlete, and it drives the target splits printed on workouts. Gold Medal Standard is the external benchmark your club configures per boat class. Every result is scored against both, so you can see effort against ambition and against the sport.',
  },
  {
    q: 'Is the mobile app the same as the web app?',
    a: 'Not quite, on purpose. The app covers the athlete\u2019s day \u2014 today\u2019s session, attendance, training log, results, equipment and meters \u2014 plus push notifications. Lineup building, analytics and club administration live on the web where there is room to work.',
  },
];

function Item({ item, open, onToggle }) {
  const reduce = useReducedMotion();
  return (
    <div className="border-b border-slate-200">
      <button
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-6 py-5 text-left"
      >
        <span className="text-base font-semibold text-slate-900">{item.q}</span>
        <motion.span
          animate={{ rotate: open ? 45 : 0 }}
          transition={{ duration: 0.25, ease: EASE }}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-500"
        >
          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M12 5v14M5 12h14" />
          </svg>
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={reduce ? false : { height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={reduce ? undefined : { height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: EASE }}
            className="overflow-hidden"
          >
            <p className="pb-5 pr-12 text-sm leading-relaxed text-slate-600">{item.a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FAQ() {
  const [open, setOpen] = useState(0);

  return (
    <section id="faq" className="border-t border-slate-200/70 bg-slate-50 py-20 sm:py-28">
      <div className="mx-auto max-w-3xl safe-area-x sm:px-6 lg:px-8">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">FAQ</p>
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            The questions clubs actually ask.
          </h2>
        </Reveal>

        <Reveal delay={0.1} className="mt-10 border-t border-slate-200">
          <div>
            {ITEMS.map((item, i) => (
              <Item
                key={item.q}
                item={item}
                open={open === i}
                onToggle={() => setOpen(open === i ? -1 : i)}
              />
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
